import { isEmpty } from './isEmpty';

/**
 * @memberof module:util
 * @function
 * @name cleanPickupDeliveryJson
 * @description It builds "form_params.pickup" and "form_params.delivery" in params and removes unwanted properties in them,
 * before making Network request in [postZipCodeData]{@link module:route_actions~postZipCodeData} and [postBookSaveChatUserData]{@link module:route_actions~postZipCodeData}.
 * It also builds port object i.e. "from" and "to" object for query params.
 * @param {Redux.Store} getState - Whole redux store state
 * @param {string} type - whether it's for "pickup" or "delivery"
 * @returns {Object}
 *
 * @example
 * console.log(cleanPickupDeliveryJson(getState, "pickup")); //{ newDataJson, selectedPortForSearchPage: {city, country, id} }
 * console.log(cleanPickupDeliveryJson(getState, "delivery")); //{ newDataJson, selectedPortForSearchPage: {city, country, id} }
 */

export const cleanPickupDeliveryJson = (getState, type) => {
  let dataJson = getState().form[type];
  const removeArr = [
    'id',
    'country',
    'city',
    'pickupJson',
    'deliveryJson',
    'existingFactoryJson',
  ];
  let selectedPortForSearchPage = {};

  let newDataJson = Object.keys(dataJson).reduce((obj, key) => {
    if (!removeArr.includes(key)) {
      obj[key] = dataJson[key];
    }
    return obj;
  }, {});

  //include selectedPort
  const pickupDeliveryType = getState().form[type][`${type}Type`];
  let selectedPort = {};
  if (pickupDeliveryType === 'port') {
    selectedPort = {
      id: dataJson.id,
      country: dataJson.country,
      city: dataJson.city,
    };
    newDataJson.selectedPort = selectedPort;
  }
  const isExistingFactorySelected = getState().form.factoryInfo[type]
    .isExistingFactorySelected;

  if (pickupDeliveryType === 'factory' && isExistingFactorySelected) {
    let selectedFactoryJson = getState().form[type].selectedExistingFactoryJson;

    if (selectedFactoryJson && !isEmpty(selectedFactoryJson)) {
      if (selectedFactoryJson.port && !isEmpty(selectedFactoryJson.port)) {
        selectedPort = selectedFactoryJson.port;
      }
    }

    newDataJson.selectedPort = selectedPort;
  }

  // for adding selectedPort to pickup/delivery for new factory type
  if (pickupDeliveryType === 'factory' && !isExistingFactorySelected) {
    selectedPort = getState().form.factoryInfo[type].newFactory.port || {};
    newDataJson.selectedPort = selectedPort;
  }

  // for delivery ecommerce and amazon mode, add selected port
  if (
    type === 'delivery' &&
    (pickupDeliveryType === 'ecommerce' || pickupDeliveryType === 'amazon')
  ) {
    const ecommAmazonModeKey =
      pickupDeliveryType === 'ecommerce' ? 'ecommerceMode' : 'amazonMode';

    const selectedFactoryMode = getState().form[type][ecommAmazonModeKey] || {};

    const ecommAmazonSelectedJsonKey =
      pickupDeliveryType === 'ecommerce'
        ? 'selectedEcommerceJson'
        : 'selectedAmazonJson';

    const selectedFactoryJson =
      selectedFactoryMode[ecommAmazonSelectedJsonKey] || {};

    if (selectedFactoryJson && !isEmpty(selectedFactoryJson)) {
      if (selectedFactoryJson.port && !isEmpty(selectedFactoryJson.port)) {
        selectedPort = selectedFactoryJson.port;
      }
    }

    newDataJson.selectedPort = selectedPort;
    newDataJson = {
      ...newDataJson,
      deliveryType: 'factory', //set it to factory not amazon or ecommerce
      factoryType: pickupDeliveryType,
      selectedExistingFactoryJson: selectedFactoryJson,
      ecommerceMode: undefined, //remove it from request params
      amazonMode: undefined, //remove it from request params
      ...(pickupDeliveryType === 'amazon' && {
        boxSelection: selectedFactoryMode.boxSelection,
        palletSelection: selectedFactoryMode.palletSelection,
      }),
    };
  }

  selectedPortForSearchPage = selectedPort;
  //only for search bar page
  if (pickupDeliveryType === 'factory' && !isExistingFactorySelected) {
    selectedPortForSearchPage = getState().form.factoryInfo[type].newFactory
      .port || {
      id: '',
      name: '',
      country: '',
    };
  }

  selectedPortForSearchPage = isEmpty(selectedPortForSearchPage)
    ? {
        id: '',
        name: '',
        country: '',
      }
    : selectedPortForSearchPage;

  return { newDataJson, selectedPortForSearchPage };
};
